import { FC, useCallback, useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { ConfirmClassChangeModal } from "@components/transactions/TransactionsBulkActions/TransactionsActionBar/ConfirmClassChangeModal";
import { buildInvoiceOrBillLockedSuffix } from "@components/transactions/matchedInvoiceOrBillLock";
import { BulkClassCommandsList } from "./BulkClassCommandsList";
import { CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

export const TransactionBulkClassCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { returnToRoot } = useCommands();
    const {
        selectedTransactionCount = 0,
        transactionBulkClassTree = [],
        transactionBulkClassesMap,
        transactionApplyClasses,
        hasExistingClassAssignments = false,
        transactionsWithExistingClassCount = 0,
        transactionsLockedByInvoiceOrBillCount = 0,
    } = options;

    const [pendingClassIds, setPendingClassIds] = useState<string[]>([]);
    const [showConfirmClassChange, setShowConfirmClassChange] = useState(false);

    // The class update only ever applies to the unlocked subset (see
    // transactionApplyClasses / useClassCommandOptions's
    // unlockedTransactionIds) — the confirmation copy must reflect that
    // actual target count, not the raw selection, so "Update Class for N
    // transactions?" never overstates what the click will change.
    const targetTransactionCount = Math.max(
        0,
        selectedTransactionCount - transactionsLockedByInvoiceOrBillCount,
    );

    const applyClasses = useCallback(
        (classIds: string[]) => {
            transactionApplyClasses?.(classIds);
            returnToRoot();
        },
        [transactionApplyClasses, returnToRoot],
    );

    const handleApply = useCallback(
        (classIds: string[]) => {
            if (hasExistingClassAssignments) {
                setPendingClassIds(classIds);
                setShowConfirmClassChange(true);
                return;
            }
            applyClasses(classIds);
        },
        [hasExistingClassAssignments, applyClasses],
    );

    const handleConfirmClassChange = useCallback(async () => {
        applyClasses(pendingClassIds);
        setShowConfirmClassChange(false);
    }, [applyClasses, pendingClassIds]);

    return (
        <>
            <BulkClassCommandsList
                heading={`Set class for ${pluralizeWithCount("transaction", selectedTransactionCount)}${buildInvoiceOrBillLockedSuffix(transactionsLockedByInvoiceOrBillCount)}`}
                classTree={transactionBulkClassTree}
                classesMap={transactionBulkClassesMap}
                onApply={handleApply}
            />
            <ConfirmClassChangeModal
                show={showConfirmClassChange}
                onHide={() => setShowConfirmClassChange(false)}
                selectedClasses={pendingClassIds}
                onConfirm={handleConfirmClassChange}
                selectedCount={targetTransactionCount}
                transactionsWithExistingClassCount={
                    transactionsWithExistingClassCount
                }
            />
        </>
    );
};
